import { useEffect, useRef, useState, FC } from 'react';
import { useHistory } from 'react-router-dom';
import * as Moment from 'moment';
import { extendMoment } from 'moment-range';
import SendNotifications from '../common/SendNotifications';

//Components
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import ActivityLoading from '../common/ActivityLoading';
import ActivityModal from '../common/ActivityModal';
import ActivityToast from '../common/ActivityToast';

//Services
import firebaseService from '../../firebase/FirebaseService';
import { getLoggedUser, getUserById } from '../../services/UserService';
import { getActivitiesByUserId, getJunctionByStudentAndActivity, addActivityToUser, removeActivityFromUser } from '../../services/NewGroupService';
import { registerUserToDatabase } from '../../services/NewGroupService';

//Types
import { ActivityInterface, ModalOptionsInterface, ToastOptionsInterface } from '../common/Types';

const moment = extendMoment(Moment);

interface InscriptionListInterface {
    admin: boolean;
    activities: Array<any>;
    cyclesIds: Array<any>;
    handleInscription: any;
    goToActivityDetails: any;
    authorized: any;
}

interface InscriptionCardInterface {
    admin: boolean;
    activity: any;
    disabled: boolean;
    handleInscription: any;
    goToActivityDetails: any;
    location?: any;
}

const InscriptionCard: FC<InscriptionCardInterface> = (props: InscriptionCardInterface) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { activity, admin, disabled, handleInscription, goToActivityDetails } = props;

    const now = moment();
    const startDate = moment(activity?.start_date.toDate());
    const endDate = moment(activity?.end_date.toDate());

    const range = moment.range(startDate, endDate);
    const inscriptionAvailable = range.contains(now);

    if (!inscriptionAvailable)
        return null;

    const totalChar = 100;
    let description = "";

    if (activity.description.length > totalChar) {
        description = activity.description.substring(0, totalChar - 3) + "...";
    } else if (activity.description.length < totalChar) {
        description = activity.description;
    } else {
        description = activity.description;
    }

    return (
        <Col key={activity.id}>
            <Card>
                <Card.Img style={{ width: '100%' }} variant="top" src={activity.image_url ? activity.image_url : "./cccr-default.jpg"} />
                <Card.Body>
                    <Card.Title>{activity.name}</Card.Title>
                    <Card.Subtitle>{`Registered: ${activity.inscribed}/${activity.students_limit}`}</Card.Subtitle>
                    <Card.Text style={{ overflow: 'hidden', textOverflow: 'ellipsis', minHeight: '100px', maxHeight: '100px', textAlign: 'justify', marginTop: '10px' }}>
                        {description}
                    </Card.Text>
                    <div style={{ display: 'flex', flexDirection: 'row', marginTop: '10px', alignItems: 'center' }}>
                        {activity.subscribed ?
                            <Button variant="primary" onClick={() => handleInscription(activity, "unsubscribe")} style={{ width: '100%' }} >Unregister</Button> :
                            <Button variant="primary" onClick={() => handleInscription(activity, "subscribe")} style={{ width: '100%' }} >Register</Button>
                        }
                        <Button variant="primary" onClick={() => goToActivityDetails(activity)} style={{ marginLeft: '10px', width: '100%' }}>Details</Button>
                    </div>
                </Card.Body>
            </Card>
        </Col>
    )
}

const CycleSection: FC<any> = (props: any) => {
    const { activities, admin, cycle, goToActivityDetails, handleInscription } = props;

    if (!admin) {
        const now = moment();
        const startDate = moment(cycle?.start_date.toDate());
        const endDate = moment(cycle?.end_date.toDate());

        const range = moment.range(startDate, endDate);
        const cycleAvailable = range.contains(now);

        if (!cycleAvailable)
            return null;
    }

    return (
        <Row key={cycle.id} xs={1} sm={2} md={2} lg={3} className="g-4">
            <div style={{ width: '100%', fontSize: '20px', fontWeight: 'bolder' }}>
                {cycle.name}
            </div>

            {activities && activities.map((activity: any, idx: any) => (
                activity.cycle === cycle.id ?
                    <Col key={idx} style={{ marginBottom: '1.5rem' }}>
                        <InscriptionCard
                            admin={admin}
                            disabled={cycle.subscribed}
                            activity={activity}
                            handleInscription={handleInscription}
                            goToActivityDetails={goToActivityDetails}
                        />
                    </Col> : null
            ))
            }
        </Row>
    )
}

const InscriptionList: FC<InscriptionListInterface> = (props: InscriptionListInterface) => {
    const { admin, activities, cyclesIds, handleInscription, goToActivityDetails, authorized } = props;

    cyclesIds?.sort((d1, d2) => d2.end_date.toDate().getTime() - d1.end_date.toDate().getTime());

    return (
        <div>
            {cyclesIds.length > 0 ?
                (cyclesIds?.map((cycle: any, idx: number) => (
                    <CycleSection key={idx} admin={admin} activities={activities} cycle={cycle} goToActivityDetails={goToActivityDetails} handleInscription={handleInscription} authorized={authorized} />
                ))) : (<SendNotifications message={(authorized?.admin ? "No hay actividades disponibles" : (authorized?.student ? "There are no activities available" : "No hay actividades disponibles"))} />)
            }
        </div>
    )
}

const Inscriptions: FC = (props: any) => {
    const { location } = props;

    const [modal, setModal] = useState<ModalOptionsInterface>({ open: false, header: "", body: "", buttons: [] });
    const [admin, setAdmin] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activities, setActivities] = useState<any>(undefined);
    const [cyclesIds, setCyclesIds] = useState<Array<any>>([]);
    const [authorized, setAuthorized] = useState<any>(undefined);
    const [user, setUser] = useState<any>(undefined);
    const [toastOptions, setToastOptions] = useState<ToastOptionsInterface>({ open: false, message: "" });

    const history = useHistory();
    let ref = useRef<any>(null);

    const activitiesListener = () => {
        firebaseService.db?.collection("activities")
            .onSnapshot((snapshot) => {
                snapshot.docChanges().forEach(async (change) => {
                    const { activities, user } = ref.current;
                    if (change.type === "modified" && activities) {
                        const modifiedActivity = activities?.find((act: any) => act.id === change.doc.id);
                        
                        if (modifiedActivity) {
                            const activitiesFiltered = activities.filter((act: any) => act.id !== change.doc.id);

                            modifiedActivity.reserved = change.doc.data().reserved;
                            modifiedActivity.inscribed = change.doc.data().inscribed;

                            const junction = await getJunctionByStudentAndActivity(user.email, change.doc.id);
                            modifiedActivity.subscribed = junction !== undefined;

                            activitiesFiltered.push(modifiedActivity);
                            setActivities(activitiesFiltered)
                        }
                    }
                });
            });
    }

    useEffect(() => {
        async function initialLoad() {
            const state = location.state ? location.state : undefined;
            let userResp: any;

            setLoading(true);
            activitiesListener();

            if (state !== undefined) {
                userResp = await getUserById(state.email);
                setAdmin(true);
            } else {
                userResp = await getLoggedUser();
            }

            if (userResp && userResp.email) {
                setUser(userResp);
                fetchUserActivities(userResp);
            } else {
                setActivities([]);
                setLoading(false);
            }

            registerUserToDatabase().then((response: any) => {
                setAuthorized(response);
            });
        }

        initialLoad();
    }, [location]);

    useEffect(() => {
        let cyclesIds: Array<any> = [];
        activities?.forEach((element: any) => {
            let cycle = cyclesIds.find((cyc) => cyc.id === element?.cycle) && element?.cycleData;
            if (!cycle) {
                cyclesIds.push({ ...element?.cycleData, subscribed: element.subscribed });
            } else {
                if (element.subscribed) {
                    cyclesIds = cyclesIds.filter((cId) => cId.id !== cycle.id);
                    cyclesIds.push({ ...element?.cycleData, subscribed: element.subscribed });
                }
            }
        });

        setCyclesIds(cyclesIds);
    }, [activities]);

    useEffect(() => {
        ref.current = { activities, user };
    }, [activities, user]);

    const fetchUserActivities = (u: any) => {
        getActivitiesByUserId(u.email).then((actResp) => {
            setUser(u);
            setActivities(actResp);
            setLoading(false);
        })
    }

    const goToActivityDetails = (activity: ActivityInterface) => {
        history.push({
            pathname: `/activityDetails/${activity.id}`,
            state: { reserve: !activity.subscribed }
        });
    }

    const handleInscription = (activity: any, type: string) => {

        if (type === "subscribe" && activity.reserved + activity.inscribed < activity.students_limit) {
            setModal({
                open: true,
                header: "Register",
                body: "Before you register, make sure you have read all the available information and the schedules of your activity. Are you sure you want to register to this activity?",
                buttons: [
                    { text: "Register", func: () => saveInscription(activity, type) },
                    { text: "Cancel", func: handleModalClose }
                ]
            });
        } else if (type === "subscribe" && activity.reserved + activity.inscribed >= activity.students_limit) {
            setModal({
                open: true,
                header: "We're sorry!",
                body: "You can't register, the student limit has been reached",
                buttons: [
                    { text: "Cancel", func: handleModalClose }
                ]
            });
        } else {
            setModal({
                open: true,
                header: "Unregister",
                body: "Are you sure you want to unregister from this activity?",
                buttons: [
                    { text: "Unregister", func: () => saveInscription(activity, type) },
                    { text: "Cancel", func: handleModalClose }
                ]
            });
        }
    }

    const saveInscription = (activity: any, type: string) => {
        setLoading(true);
        if (activity && user?.email) {
            if (type === "subscribe") {
                addActivityToUser(user.email, activity.id).then((response) => {
                    fetchUserActivities(user);
                    handleModalClose();

                    if (response.code === "success") {
                        setToastOptions({ open: true, message: response.message });
                    } else if (response.code === "full_group") {
                        setToastOptions({ open: true, message: response.message });
                    }
                })
            } else {
                removeActivityFromUser(user.email, activity.id).then((response) => {
                    fetchUserActivities(user);
                    handleModalClose();

                    if (response.code === "success") {
                        setToastOptions({ open: true, message: response.message });
                    }
                })
            }
        }
    }

    const handleModalClose = () => {
        setModal({ open: false, header: "", body: "", buttons: [] });
    }

    if (loading) return <ActivityLoading />

    activities?.sort((a1: any, a2: any) => a1.name.localeCompare(a2.name));

    return (
        <Container>
            <div style={{ marginBottom: '20px' }}>
                <h1>Registration</h1>
            </div>
            <ActivityModal {...modal} onClose={handleModalClose} />
            <ActivityToast {...toastOptions} onClose={() => setToastOptions({ open: false, message: "" })} autohide={4000} />
            <InscriptionList
                admin={admin}
                activities={activities}
                cyclesIds={cyclesIds}
                handleInscription={handleInscription}
                goToActivityDetails={goToActivityDetails}
                authorized={authorized}
            />
        </Container>
    )
}

export default Inscriptions;
